import {
  type AnyRecord,
  REVIEW_REASON,
  type RecordApiKind,
  type ReviewQueueKind,
  reviewActionLabel,
  SUBMISSION_NOTE_MAX,
} from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Textarea, useToast } from '@fruuit/ui'
import { useId, useState } from 'react'
import { useApiMutation } from '../../api/hooks'
import { recordsApi, useInvalidateRecords } from '../records/api'
import {
  ACTION_EXPLANATION,
  ACTION_TOAST,
  REASON_LABEL,
  type ReviewDialogAction,
  reviewErrorMessage,
} from './copy'

export interface ReviewActionTarget {
  kind: RecordApiKind
  id: string
  revision: number
  /** Shown in the dialog title ("Approve assessment · Kestrelmoor Mining v3"). */
  title?: string
}

export interface ReviewActionDialogProps {
  action: ReviewDialogAction | null
  target: ReviewActionTarget | null
  onClose: () => void
  onDone?: (record: AnyRecord) => void
}

/**
 * Reason dialog for submit, approve, publish, return, retract and release (FORM-009, WF-006).
 * Single-flight: the button stays busy until the server answers, and a retry of the same
 * reason replays the same request id (D-14).
 */
export function ReviewActionDialog({ action, target, onClose, onDone }: ReviewActionDialogProps) {
  if (!action || !target) return null
  return (
    <ReviewActionDialogBody
      key={`${target.kind}:${target.id}:${action}`}
      action={action}
      target={target}
      onClose={onClose}
      onDone={onDone}
    />
  )
}

function ReviewActionDialogBody({
  action,
  target,
  onClose,
  onDone,
}: {
  action: ReviewDialogAction
  target: ReviewActionTarget
  onClose: () => void
  onDone: ((record: AnyRecord) => void) | undefined
}) {
  const formId = useId()
  const { toast } = useToast()
  const invalidate = useInvalidateRecords()
  const [reason, setReason] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const optional = action === 'submit'
  const max = optional ? SUBMISSION_NOTE_MAX : REVIEW_REASON.max
  const label = reviewActionLabel(target.kind as ReviewQueueKind, action)

  const mutation = useApiMutation(
    (body: { reason: string }, ctx) => {
      const payload = { revision: target.revision, reason: body.reason || null }
      if (action === 'release') {
        return recordsApi.release(target.kind, target.id, { ...payload, reason: body.reason }, ctx)
      }
      return recordsApi.review(target.kind, target.id, action, payload, ctx)
    },
    {
      onSuccess: async (record) => {
        await invalidate()
        toast({ tone: 'success', message: ACTION_TOAST[action] })
        onDone?.(record as AnyRecord)
        onClose()
      },
      onError: (error) => setFormError(reviewErrorMessage(error)),
    },
  )

  function submit() {
    const trimmed = reason.trim()
    if (!optional && trimmed.length < REVIEW_REASON.min) {
      setFieldError(
        `Record a substantive reason (${REVIEW_REASON.min}–${REVIEW_REASON.max} characters).`,
      )
      return
    }
    setFieldError(null)
    setFormError(null)
    mutation.mutate({ reason: trimmed })
  }

  const destructive = action === 'retract' || action === 'return'

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={label}
      description={target.title}
      footer={
        <>
          <DialogCancel />
          <Button
            type="submit"
            form={formId}
            variant={destructive ? 'danger' : 'primary'}
            loading={mutation.isPending}
          >
            {label}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
        className="flex flex-col gap-4"
      >
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
        <p className="text-sm leading-relaxed text-ink-soft">{ACTION_EXPLANATION[action]}</p>
        <Textarea
          label={REASON_LABEL[action]}
          hint={
            optional
              ? 'Optional. The reviewer sees it with the record.'
              : `At least ${REVIEW_REASON.min} characters. Kept in the review history and the audit trail.`
          }
          required={!optional}
          rows={4}
          maxLength={max}
          minLength={optional ? undefined : REVIEW_REASON.min}
          value={reason}
          error={fieldError}
          disabled={mutation.isPending}
          onChange={(event) => {
            setReason(event.target.value)
            if (fieldError) setFieldError(null)
          }}
        />
      </form>
    </Dialog>
  )
}
